import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useQueryParams, serializeArrayParam } from 'hooks/useQueryParams';
import { MapSearchURLParams } from 'hooks/useMapSearch';
import { useIncidentFiltersState } from '../../../state/useIncidentFiltersState';
import { parseFiltersFromURLParams } from './SearchBar.utils';

/**
 * Custom hook to sync incident filters between state and URL params.
 * Only syncs on the home route.
 */
export const useSyncFiltersWithUrlParams = (): void => {
  const location = useLocation();
  const isOnHomeRoute = location.pathname === '/';
  const [queryParams, setParams] = useQueryParams<MapSearchURLParams>();
  const { ownership, urgency, metadata } = queryParams;
  const { filters, setFilters } = useIncidentFiltersState();
  const initialized = useRef(false);

  // Initialize filter state from URL params on first mount only
  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    if (!isOnHomeRoute) return;

    if (ownership || urgency || metadata) {
      const parsedFilters = parseFiltersFromURLParams({
        ownership,
        urgency,
        metadata,
      });
      setFilters(parsedFilters);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only run on mount
  }, []);

  // Sync filter state to URL params when filters change
  useEffect(() => {
    if (!isOnHomeRoute) return;

    const currentParams = Object.fromEntries(
      new URLSearchParams(window.location.search),
    ) as MapSearchURLParams;

    setParams({
      ...currentParams,
      ownership: filters.ownership,
      urgency: serializeArrayParam(filters.urgency),
      metadata: serializeArrayParam(filters.metadata),
    });
  }, [isOnHomeRoute, filters, setParams]);
};
